/**
 * RestrictedRevenueCard — Total Ecosystem Revenue, hidden until authorised.
 *
 * The figure is never fetched speculatively: an unauthorised browser is never
 * sent it, and an authorised one only asks for it when the person deliberately
 * chooses to look, so it cannot flash onto a shared screen on login.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldAlert, Lock, Eye, Clock, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body.detail;
    const message = typeof detail === 'string' ? detail
      : (detail?.reason || detail?.msg || `Request failed (${res.status})`);
    const err = new Error(message);
    err.detail = detail;
    throw err;
  }
  return body;
};

const RESOURCE = 'ecosystem_revenue';

export const RestrictedRevenueCard = ({ currency = 'GBP' }) => {
  const qc = useQueryClient();
  const [stage, setStage] = useState(null);     // warning | request | confirm
  const [duration, setDuration] = useState('3h');
  const [reason, setReason] = useState('');
  const [emergency, setEmergency] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['sensitive-status', RESOURCE],
    queryFn: () => api(`/finance/sensitive/status?resource=${RESOURCE}`),
    refetchInterval: 60000,
  });

  const { data: options } = useQuery({
    queryKey: ['sensitive-options'],
    queryFn: () => api('/finance/sensitive/options'),
    enabled: stage === 'request',
  });

  // Only ever requested after a deliberate choice to view.
  const { data: revenue, isFetching: loadingFigure } = useQuery({
    queryKey: ['ecosystem-revenue'],
    queryFn: () => api('/finance/ledger/ecosystem-revenue'),
    enabled: revealed && status?.state === 'active',
    staleTime: 60000,
    gcTime: 0,                 // not kept in the cache once the card unmounts
  });

  const state = status?.state || 'none';

  const openWarning = async () => {
    setBusy(true);
    try {
      await api('/finance/sensitive/attempt', {
        method: 'POST', body: JSON.stringify({ resource: RESOURCE }),
      });
      setStage('warning');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async () => {
    if (!reason.trim()) { toast.error('A reason for access is required'); return; }
    setBusy(true);
    try {
      await api('/finance/sensitive/requests', {
        method: 'POST',
        body: JSON.stringify({ resource: RESOURCE, reason, duration }),
      });
      toast.success('Request sent to the Financial Information Custodians');
      setStage(null); setReason('');
      qc.invalidateQueries({ queryKey: ['sensitive-status', RESOURCE] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitEmergency = async () => {
    if (emergency.trim().length < 30) {
      toast.error('Say what has happened and why it cannot wait for a custodian');
      return;
    }
    setBusy(true);
    try {
      const res = await api('/finance/sensitive/emergency', {
        method: 'POST',
        body: JSON.stringify({ resource: RESOURCE, justification: emergency }),
      });
      toast.success(`Raised — another person must countersign within ${res.expires_in_minutes} minutes`);
      setStage(null); setEmergency('');
      qc.invalidateQueries({ queryKey: ['sensitive-status', RESOURCE] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openOneTime = async () => {
    setBusy(true);
    try {
      await api(`/finance/sensitive/grants/${status.grant_id}/redeem`, { method: 'POST' });
      qc.invalidateQueries({ queryKey: ['sensitive-status', RESOURCE] });
      setRevealed(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const expiry = status?.expires_at ? new Date(status.expires_at) : null;

  return (
    <Card className="mt-4 border-amber-500/30 bg-amber-50 dark:bg-slate-900"
          data-testid="stat-ecosystem-revenue">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-300 flex items-center gap-2">
              <Lock className="h-4 w-4" /> Total Ecosystem Revenue
            </p>

            {isLoading ? (
              <p className="text-3xl font-bold text-slate-600 dark:text-slate-300 mt-2">
                <Loader2 className="h-6 w-6 animate-spin inline" />
              </p>
            ) : revealed && state === 'active' && revenue ? (
              <div className="mt-2" data-testid="revenue-revealed">
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  {Object.entries(revenue.total_income_by_currency || {}).map(([cur, amt]) => (
                    <span key={cur} className="text-3xl font-bold"
                          data-testid={`ecosystem-revenue-${cur.toLowerCase()}`}>
                      {cur} {Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  ))}
                  {Object.keys(revenue.total_income_by_currency || {}).length === 0 && (
                    <span className="text-3xl font-bold text-slate-600 dark:text-slate-300">0.00</span>
                  )}
                  {loadingFigure && <Loader2 className="h-5 w-5 animate-spin" />}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(revenue.by_workspace || []).slice(0, 6).map((w) => {
                    const total = Object.values(w.revenue_by_currency || {})
                      .reduce((a, b) => a + Number(b || 0), 0);
                    return (
                      <Badge key={w.workspace} variant="outline"
                             data-testid={`workspace-${w.workspace}`}>
                        {w.workspace} · {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-3">
                  Your viewing of this information has been logged.
                  {expiry && ` Access expires ${expiry.toLocaleString()}.`}
                </p>
              </div>
            ) : (
              <>
                <p className="text-3xl font-bold tracking-widest text-slate-600 dark:text-slate-300 mt-2"
                   data-testid="revenue-masked">
                  {currency} ••••••••
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1" data-testid="revenue-state">
                  {state === 'pending'
                    ? `Access request pending approval · ${status.approvals || 0} of ${status.approvals_required || 1} custodians`
                    : state === 'awaiting_countersign'
                      ? 'Emergency access raised — waiting for a second person to countersign'
                      : state === 'active'
                      ? `Access granted — expires ${expiry ? expiry.toLocaleString() : ''}`
                      : state === 'approved_awaiting_open'
                        ? 'One-time access approved — open it when you are ready'
                        : state === 'expired' ? 'Access expired — request renewal'
                          : state === 'revoked' ? 'Access revoked — request again'
                            : 'Restricted'}
                </p>
              </>
            )}
          </div>

          <div className="shrink-0 space-y-2">
            {state === 'active' && !revealed && (
              <Button size="sm" onClick={() => setStage('confirm')}
                      data-testid="view-restricted-btn">
                <Eye className="h-4 w-4 mr-1" /> View Financial Information
              </Button>
            )}
            {state === 'approved_awaiting_open' && (
              <Button size="sm" onClick={openOneTime} disabled={busy}
                      data-testid="open-one-time-btn">
                <Clock className="h-4 w-4 mr-1" /> Open one-time access
              </Button>
            )}
            {['none', 'pending', 'expired', 'revoked'].includes(state) && (
              <Button size="sm" variant="outline" onClick={openWarning} disabled={busy}
                      data-testid="request-restricted-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      : <ShieldAlert className="h-4 w-4 mr-1" />}
                {state === 'pending' ? 'Request pending'
                  : state === 'expired' || state === 'revoked' ? 'Request renewal'
                    : 'View Restricted Financial Information'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>

      <Dialog open={stage === 'warning'} onOpenChange={(v) => !v && setStage(null)}>
        <DialogContent className="max-w-lg" data-testid="restricted-warning-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Restricted financial information
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line max-h-[50vh] overflow-auto">
            {options?.warning || `You are attempting to access confidential financial information belonging to the Faith World Ministries ecosystem.

This information is restricted and is not automatically available to administrators or Super Administrators.

Your attempt to access this information will be recorded in the FWM security audit log. The record may include your identity, date and time, requested resource, organisational scope and relevant security information.

Access requires explicit authorisation and may be granted for a limited period only.

Do you wish to continue?`}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStage(null)}
                    data-testid="warning-cancel-btn">Cancel</Button>
            <Button onClick={() => setStage('request')} data-testid="warning-continue-btn">
              Continue to Access Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stage === 'request'} onOpenChange={(v) => !v && setStage(null)}>
        <DialogContent className="max-w-md" data-testid="access-request-dialog">
          <DialogHeader>
            <DialogTitle>Request permission to view</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>How long do you need it for?</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="mt-1" data-testid="request-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.durations || [{ id: '3h', label: '3 hours' }]).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Why do you need to see it?</Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Preparing the trustees' report for the October meeting"
                        className="mt-1" data-testid="request-reason-input" />
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              A Financial Information Custodian decides this — not a platform administrator —
              and may grant a shorter period than you ask for.
            </p>
            <button type="button" onClick={() => setStage('emergency')}
                    className="text-xs text-rose-600 hover:underline"
                    data-testid="emergency-link">
              This cannot wait for a custodian
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStage(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitRequest} disabled={busy} data-testid="submit-request-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stage === 'emergency'} onOpenChange={(v) => !v && setStage(null)}>
        <DialogContent className="max-w-md" data-testid="emergency-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Emergency access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This is for a genuine crisis when no custodian can be reached. It is
              not a way round them:
            </p>
            <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc pl-5 space-y-1">
              <li>another person has to countersign it within 15 minutes, or it lapses</li>
              <li>it lasts one hour and cannot be renewed or extended</li>
              <li>every custodian is told it happened, and it is recorded as an emergency</li>
            </ul>
            <div>
              <Label>What has happened?</Label>
              <Textarea rows={3} value={emergency}
                        onChange={(e) => setEmergency(e.target.value)}
                        placeholder="The bank has frozen the HQ account this afternoon and the trustees need the consolidated position before 5pm"
                        className="mt-1" data-testid="emergency-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStage('request')} disabled={busy}>Back</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitEmergency}
                    disabled={busy} data-testid="submit-emergency-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Raise emergency access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stage === 'confirm'} onOpenChange={(v) => !v && setStage(null)}>
        <DialogContent className="max-w-md" data-testid="access-approved-dialog">
          <DialogHeader>
            <DialogTitle>Access approved</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
            <p>You have temporary permission to view the selected financial information.</p>
            {expiry && <p>Your access expires on <strong>{expiry.toLocaleString()}</strong>.</p>}
            <p>All access to this information is logged.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStage(null)}>Cancel</Button>
            <Button onClick={() => { setRevealed(true); setStage(null); }}
                    data-testid="confirm-view-btn">
              View Financial Information
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RestrictedRevenueCard;
